import { Box, Button, Group, Tooltip } from "@mantine/core";
import { IconCalendarExclamation } from "@tabler/icons-react";
import type { DateTimeFormatOptions, DurationLike } from "luxon";
import { DateTime } from "luxon";
import type { MRT_ColumnDef, MRT_RowData } from "mantine-react-table-open";
import { MantineIconCell } from "../../../../mantine/mrt/components/cell/MantineIconCell";
import { LOCAL_DATE_SETTINGS } from "../../../../utils/dates";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { isDateInFutureDuration } from "../../../../utils/dates/isDateInFutureDuration";
import type { ColumnDefinitionProps } from "./columns.types";

interface DateEndColumnProps<
  T extends MRT_RowData
> extends ColumnDefinitionProps<T> {
  showExpiringSoonIndicator?: boolean;
  expiringSoonThresholdDuration?: DurationLike;
  getExpiringSoonOrExpiredTooltipText?: (object: T) => string;
  getShowExtensionButton?: (object: T) => boolean;
  extensionButtonText?: string;
  onClickExtend?: (object: T) => void;
  DateTimeFormatOptions?: DateTimeFormatOptions;
}

function getDateEndColumn<T extends MRT_RowData>({
  key,
  options,
  showExpiringSoonIndicator = false,
  expiringSoonThresholdDuration = { months: 3, weeks: 2 },
  getExpiringSoonOrExpiredTooltipText = () => "Der Zeitraum läuft bald ab",
  getShowExtensionButton,
  extensionButtonText = "Verlängern",
  onClickExtend,
  DateTimeFormatOptions = LOCAL_DATE_SETTINGS
}: DateEndColumnProps<T>): MRT_ColumnDef<T> {
  return {
    id: key,
    header: "Zeitraum Ende",
    accessorFn: (row) =>
      row?.[key]
        ? backendDateOrDateTimeToLuxonDateTime(row?.[key]).toJSDate()
        : undefined,
    filterVariant: "date",
    filterFn: (row, columnId, filterValue) => {
      const cellDate = DateTime.fromJSDate(row.getValue<Date>(columnId));
      if (!cellDate.isValid) return true;
      const endDate = DateTime.fromISO(filterValue || "2100-01-01");
      return cellDate <= endDate;
    },
    filterTooltipValueFn: (filterValue) =>
      filterValue
        ? `vor ${DateTime.fromISO(filterValue).toLocaleString(DateTimeFormatOptions)}`
        : "",
    Cell: ({ cell }) => {
      const date = cell.getValue<Date>();

      if (!date) {
        return <></>;
      }

      const endOfDay = DateTime.fromJSDate(date).endOf("day");
      const isExpiringSoon = isDateInFutureDuration(
        endOfDay.toJSDate(),
        expiringSoonThresholdDuration
      );
      const isExpired = endOfDay < DateTime.now();
      const showWarning =
        showExpiringSoonIndicator && (isExpiringSoon || isExpired);
      const formattedDate = DateTime.fromJSDate(date).toLocaleString(
        DateTimeFormatOptions
      );
      const showExtensionButton = getShowExtensionButton?.(cell.row.original);

      return (
        <Group>
          {showWarning ? (
            <Tooltip
              label={getExpiringSoonOrExpiredTooltipText?.(cell.row.original)}
            >
              <Box>
                <MantineIconCell
                  iconColor="var(--mantine-color-warning-filled)"
                  IconComponent={IconCalendarExclamation}
                  renderedCellValue={formattedDate}
                />
              </Box>
            </Tooltip>
          ) : (
            formattedDate
          )}
          {showExtensionButton && (
            <Button
              size="compact-xs"
              td="underline"
              variant="transparent"
              onClick={() => onClickExtend?.(cell.row.original)}
            >
              {extensionButtonText}
            </Button>
          )}
        </Group>
      );
    },
    ...options
  };
}

export { getDateEndColumn };
